export interface AnalyticsEvents {
  site_viewed: { site_id: string; site_name: string; neighborhood: string; source: 'list' | 'map' | 'search' };
  site_favorited: { site_id: string; site_name: string };
  itinerary_updated: { action: 'add' | 'remove' | 'reorder'; size: number };
  search_performed: { query_length: number; results_count: number };
  filter_applied: { filter_type: string; value: string };
  tour_viewed: { tour_id: string; tour_name: string };
  directions_opened: { site_id: string; site_name: string; mode: string };
  notification_opened: { notification_id: string };
  app_installed: Record<string, never>;
}

/**
 * Readable name for a site id, read from the sites store's id map.
 *
 * The ids are Airtable record ids (`recXXXX`), which are unreadable in reporting —
 * every "top sites" chart showed record ids until these names were added. The names
 * are required on the events above so a missed call site fails the build.
 *
 * site_favorited fires from inside the Pinia store, which only holds the id, hence
 * the lookup. Analytics must never be the reason a favorite fails, so a map that has
 * not been fetched yet, an unknown id, or a record without the field all yield ''.
 */
export function siteNameFrom(map: Record<string, any> | undefined, id: string): string {
  return map?.[id]?.fields?.['Site'] ?? '';
}

const MAX_BUFFER = 100;

type Capture = (event: string, props: any) => void;

function createAnalyticsQueue() {
  let buffer: Array<[string, any]> = [];
  let capture: Capture | null = null;

  return {
    push(event: string, props: any): void {
      if (capture) {
        capture(event, props);
        return;
      }
      buffer.push([event, props]);
      if (buffer.length > MAX_BUFFER) buffer.shift();
    },
    setReady(fn: Capture): void {
      capture = fn;
      const drained = buffer;
      buffer = [];
      for (const [event, props] of drained) capture(event, props);
    },
    _resetForTests(): void {
      buffer = [];
      capture = null;
    },
  };
}

export const _analyticsQueue = createAnalyticsQueue();
